import { useState, useCallback } from "react"
import { toast } from "react-toastify"
import { useAdForgeGenerateImageMutation } from "~/ui-rtk/api/adForgeApi"
import {
  assetToScrapedImage,
  type PlacementAssets,
  type ScrapedImage,
} from "../types"

/**
 * `landscape` (1.91:1) is legacy — Facebook Feed now takes the same 4:5
 * portrait image as the Instagram feeds, so nothing generates or ships a
 * 1.91:1 variant any more. The key stays in the union so drafts saved before
 * the change still deserialize (and re-serialize) without losing data.
 */
type SlotKey = "landscape" | "portrait" | "stories" | "square"

interface SlotConfig {
  slot: SlotKey
  label: string
  aspectRatio: "16:9" | "4:5" | "9:16" | "1:1"
  displayRatio: string
}

export const SLOTS: SlotConfig[] = [
  {
    slot: "portrait",
    label: "Feed (4:5)",
    aspectRatio: "4:5",
    displayRatio: "4:5",
  },
  {
    slot: "stories",
    label: "Stories (9:16)",
    aspectRatio: "9:16",
    displayRatio: "9:16",
  },
  {
    slot: "square",
    label: "Square (1:1)",
    aspectRatio: "1:1",
    displayRatio: "1:1",
  },
]

/**
 * Map a backend `metadata.aspectRatio` string to the local placement slot key.
 * Anything unrecognized returns null — better to skip than land in the wrong
 * slot. Shared by AdForgePage's auto-prefill effect and the background
 * image-gen apply step so both interpret variant ratios identically.
 */
export function slotForRatio(ratio: string | undefined): SlotKey | null {
  switch (ratio) {
    case "1.91:1":
    case "16:9":
      return "landscape"
    case "4:5":
      return "portrait"
    case "9:16":
      return "stories"
    case "1:1":
      return "square"
    default:
      return null
  }
}

export function buildExtendPrompt(ratio: string): string {
  return `Extend this exact photograph to a ${ratio} aspect ratio by ONLY continuing the existing scene outward — as if the same camera captured a slightly wider view of the same moment. Keep the original subject, product, and composition unchanged and in their exact positions; the new area is just more of the surroundings already present at the edges (the same wall, floor, ground, sky, table, or background that is already there). Do NOT invent or add any new people, objects, products, props, furniture, text, logos, watermarks, patterns, or scenery, and do NOT change the style or mood — only plausibly continue what already exists. Match the original lighting, shadows, color palette, focal length, depth of field, grain, and photographic style exactly so the seam is invisible and the whole image reads as one real photo taken at ${ratio}. No borders, frames, letterboxing, solid color bars, gray/black padding, blur, or vignettes — every pixel must be continuous, realistic scene content.`
}

interface ExtendOptions {
  /** Subset of placement slots to generate. Defaults to all four ratios. */
  slots?: SlotKey[]
  /** Skip slots already populated for an asset. Defaults to true. */
  skipExisting?: boolean
  /** Override the default uncrop prompt. Used by the per-placement
   *  "custom prompt" editor so the user can steer a single Imagen call
   *  without rebuilding the rest of the extend wiring. */
  customPrompt?: string
  /** Suppress the shared progress bar + toasts. Used when many extend calls
   *  run concurrently (e.g. bulk "Enhance all") so they don't fight over the
   *  global progress state or spam one toast per card — the caller shows its
   *  own aggregate feedback instead. */
  silent?: boolean
}

export function useExtendPlacements() {
  const [generateImage] = useAdForgeGenerateImageMutation()

  const [isExtending, setIsExtending] = useState(false)
  const [progress, setProgress] = useState<{
    done: number
    total: number
    label: string
  } | null>(null)

  const extendAll = useCallback(
    async (
      assets: ScrapedImage[],
      currentByAsset: Record<string, PlacementAssets>,
      options: ExtendOptions = {},
    ): Promise<Record<string, PlacementAssets>> => {
      const slots = (options.slots ?? SLOTS.map(s => s.slot)) as SlotKey[]
      const skipExisting = options.skipExisting ?? true
      const silent = options.silent ?? false
      const targets: Array<{ asset: ScrapedImage; cfg: SlotConfig }> = []
      for (const asset of assets) {
        if (asset.type === "video") continue
        for (const slot of slots) {
          const cfg = SLOTS.find(s => s.slot === slot)
          if (!cfg) continue
          if (skipExisting && currentByAsset[asset.id]?.[slot]) continue
          targets.push({ asset, cfg })
        }
      }
      if (targets.length === 0) {
        if (!silent) toast.info("All placements already filled")
        return currentByAsset
      }

      if (!silent) {
        setIsExtending(true)
        setProgress({ done: 0, total: targets.length, label: "Starting…" })
      }
      const next: Record<string, PlacementAssets> = { ...currentByAsset }
      let failures = 0

      const missingAssetIds = new Set<string>()
      const errorMessages = new Map<string, number>()
      for (let i = 0; i < targets.length; i++) {
        const { asset, cfg } = targets[i]
        setProgress({
          done: i,
          total: targets.length,
          label: `Card ${assets.indexOf(asset) + 1} → ${cfg.displayRatio}`,
        })
        if (!asset.assetId) {
          // Without a library id we can't run the asset-id-based AI flow.
          missingAssetIds.add(asset.id)
          failures++
          continue
        }
        try {
          // Generate AND persist in a single server-side call. Passing
          // `parentAssetId` makes the backend save the generated bytes as a
          // GEN_AI child (tagged generatedBy/aspectRatio, denormalized onto the
          // parent per slot) and hand back the saved asset. We deliberately do
          // NOT take the returned data URL and `fetch()` it into a File on the
          // client — that client-side `fetch(data:)` is blocked by the prod
          // CSP/proxy allow-list, which is why this flow worked locally but
          // failed on prod. customPrompt (if non-empty) wins; otherwise fall
          // back to the uncrop template.
          const promptForCall =
            options.customPrompt?.trim() || buildExtendPrompt(cfg.displayRatio)
          const result = await generateImage({
            referenceAssetIds: [asset.assetId],
            prompt: promptForCall,
            aspectRatio: cfg.displayRatio,
            parentAssetId: asset.assetId,
            generatedBy: "ai_extend",
            placementSlot: cfg.slot,
          }).unwrap()

          if (!result.asset) {
            throw new Error("Generation returned no saved asset")
          }
          const uploaded = assetToScrapedImage(result.asset)
          const prevForAsset = next[asset.id] ?? {}
          next[asset.id] = { ...prevForAsset, [cfg.slot]: uploaded }
        } catch (err) {
          const msg =
            (err as { data?: { message?: string } })?.data?.message ||
            (err as Error)?.message ||
            "AI extend failed"
          errorMessages.set(msg, (errorMessages.get(msg) ?? 0) + 1)
          // eslint-disable-next-line no-console
          console.error(
            `[useExtendPlacements] card ${assets.indexOf(asset) + 1} → ${cfg.displayRatio} failed:`,
            err,
          )
          failures++
        }
      }

      setProgress({
        done: targets.length,
        total: targets.length,
        label: "Done",
      })
      setIsExtending(false)
      setTimeout(() => setProgress(null), 1500)

      const ok = targets.length - failures
      const onlyMissingIds =
        missingAssetIds.size > 0 && errorMessages.size === 0
      const topReason =
        [...errorMessages.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
      const failureSuffix = onlyMissingIds
        ? "source asset isn't in the library yet — re-upload from the drawer"
        : topReason
          ? `last error: ${topReason}`
          : "check console for details"
      if (failures === 0) {
        toast.success(`Extended ${ok} placement${ok === 1 ? "" : "s"}`)
      } else if (ok > 0) {
        toast.warning(
          `${ok}/${targets.length} succeeded — ${failures} failed (${failureSuffix})`,
        )
      } else {
        toast.error(`All placement extensions failed — ${failureSuffix}`)
      }
      return next
    },
    [generateImage],
  )

  return { extendAll, isExtending, progress }
}
